import 'bootstrap/dist/css/bootstrap.min.css';
import { lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import "./App.css";
import Header from "./Components/Header";
import Footer from "./Components/Footer";
import Home from "./Pages/Home";
import BackToTopButton from "./Components/BackToTopButton.js";
import { AuthProvider } from "./Context/AuthContext";
import useAuth from "./Hooks/useAuth";
import Breadcrumbs from "./Components/Breadcrumbs.js";
import { BreadcrumbProvider } from "./Context/BreadcrumbContext.js";
import routeImports from "./routeImports.js";

// Every route below is only needed after the first paint, so it is split
// into its own chunk instead of shipping inside the bundle Home depends on.
const About = lazy(routeImports["/about"]);
const KRMU = lazy(routeImports["/krmangalam-university"]);
const DeveloperTeam = lazy(routeImports["/developers"]);
const ForgotPassword = lazy(routeImports["/forgot-password"]);
const ContactForm = lazy(routeImports["/contact"]);
const Archives = lazy(routeImports["/archives"]);
const CurrentIssue = lazy(routeImports["/current-issue"]);
const AbstractingandIndexing = lazy(routeImports["/abstracting-indexing"]);
const EditorialBoard = lazy(routeImports["/editorial-leadership"]);
const Reviewers = lazy(routeImports["/reviewers"]);
const AuthorGuidelines = lazy(routeImports["/author-guidelines"]);
const LatexTemplate = lazy(routeImports["/author-guidelines/latex-template"]);
const PrivacyPolicy = lazy(routeImports["/privacy-policy"]);
const TermsandConditions = lazy(routeImports["/terms-and-conditions"]);
const Ethics = lazy(routeImports["/ethics"]);
const ITMEPoliciesFramework = lazy(routeImports["/itme-policies-framework"]);
const Form = lazy(routeImports["/form"]);
const Login = lazy(routeImports["/login"]);
const Signup = lazy(routeImports["/signup"]);
const Dashboard = lazy(routeImports["/dashboard"]);
const ReviewerDashboard = lazy(routeImports["/reviewer/dashboard"]);
const Profile = lazy(routeImports["/profile"]);
const Admin = lazy(routeImports["/admin"]);

// Param routes have no single static nav link to prefetch from, so they are
// not part of routeImports — split them the same way regardless.
const ArticlePage = lazy(() => import("./Pages/ArticlePage.js"));
const VolumePage = lazy(() => import("./Pages/VolumePage.js"));
const ViewPaper = lazy(() => import("./Pages/ViewPaper.js"));
const ReviewerPage = lazy(() => import("./Pages/ReviewerPage.js"));
const InvitePage = lazy(() => import("./Pages/InvitePage.js"));

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <BreadcrumbProvider>
            <Header />
            <div id="main">
              <div id="main_body">
                <br />
                <Breadcrumbs />
                <Suspense fallback={<div className="route-loading" aria-hidden="true" />}>
                  <AuthRoutes />
                </Suspense>
              </div>
            </div>
            <BackToTopButton />
            <Footer name="ITME" />
          </BreadcrumbProvider>
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

function AuthRoutes() {
  const { isAuthenticated, role } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
  <Route path="/krmangalam-university" element={<KRMU />} />
      <Route path="/developers" element={<DeveloperTeam />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/contact" element={<ContactForm />} />
      <Route path="/archives" element={<Archives />} />
      <Route path="/archives/vol/:volumeId/iss/:issueId/art/:articleId" element={<ArticlePage />} />
      <Route path="/archives/:quarter/:volumeNumber" element={<VolumePage />} />
      <Route path="/current-issue" element={<CurrentIssue />} />
      <Route
        path="/abstracting-indexing"
        element={<AbstractingandIndexing />}
      />
      <Route path="/editorial-leadership" element={<EditorialBoard />} />
      <Route path="/editorial-board" element={<Navigate to="/editorial-leadership" replace />} />
  <Route path="/reviewers" element={<Reviewers />} />
    <Route path="/author-guidelines" element={<AuthorGuidelines />} />
      <Route path="/author-guidelines/latex-template" element={<LatexTemplate />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-and-conditions" element={<TermsandConditions />} />
      <Route path="/ethics" element={<Ethics />} />
      <Route path="/itme-policies-framework" element={<ITMEPoliciesFramework />} />
      {isAuthenticated ? (
        <>
          {role === "reviewer" ? (
            <Route path="/reviewer/dashboard" element={<ReviewerDashboard />} />
          ) : (
            <>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/form" element={<Form />} />
            </>
          )}
          <Route path="/profile" element={<Profile />} />
          <Route path="/admin" element={<Admin />} />
          {role === "admin" && (
            <Route path="/reviewer/page/:id" element={<ReviewerPage />} />
          )}
          <Route path="/view/:paperId" element={<ViewPaper />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/signup" element={<Navigate to="/" replace />} />
        </>
      ) : (
        <>
          {/* Public access to manuscript form during early phase */}
          <Route path="/form" element={<Form />} />
          <Route path="/admin" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
        </>
      )}
      <Route path="/reviewer/invite/:inviteid" element={<InvitePage />} />
      <Route path="*" element={<h1>404, Page Not Exist!!!</h1>} />
    </Routes>
  );
}

export default App;
